import React, { useState, useEffect, useCallback } from 'react';
import type {
  User,
  UserFilters,
  CreateUserInput,
  UpdateUserInput,
  UserInvitation,
  CreateInvitationInput,
} from '@lnr-v2/types';
import { Button, Icon } from '@lnr-v2/ui';
import { AdminLayout } from '../../layouts/AdminLayout';
import { UsersTable } from '../../components/organisms/UsersTable';
import { UserTableFilters } from '../../components/organisms/UserTableFilters';
import { UserForm } from '../../components/organisms/UserForm';
import { InvitationModal } from '../../components/molecules/InvitationModal';
import { InvitationList } from '../../components/organisms/InvitationList';
import { Pagination } from '../../components/molecules/Pagination';
import { userService } from '../../services/userService';
import { invitationService } from '../../services/invitationService';
import styles from './UsersPage.module.css';

export const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [filters, setFilters] = useState<UserFilters>({
    search: '',
    role: 'all',
    status: 'all',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<User | null>(null);

  // Invitation state
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<
    UserInvitation[]
  >([]);
  const [invitationsLoading, setInvitationsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'users' | 'invitations'>('users');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const result = await userService.getUsers(currentPage, pageSize, filters);
      setUsers(result.users);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters]);

  // Fetch users whenever filters, page, or pageSize change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Fetch invitations when tab changes
  useEffect(() => {
    if (activeTab === 'invitations') {
      fetchPendingInvitations();
    }
  }, [activeTab]);

  const handleSelectUser = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map((user) => user.id));
    }
  };

  const handleCreateUser = () => {
    setEditingUser(null);
    setIsFormOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setIsFormOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setDeleteConfirm(user);
  };

  const handleFormSubmit = async (data: CreateUserInput | UpdateUserInput) => {
    if (editingUser) {
      await userService.updateUser(editingUser.id, data as UpdateUserInput);
    } else {
      await userService.createUser(data as CreateUserInput);
    }
    await fetchUsers();
    setIsFormOpen(false);
    setEditingUser(null);
  };

  const confirmDelete = async () => {
    if (deleteConfirm) {
      await userService.deleteUser(deleteConfirm.id);
      await fetchUsers();
      setDeleteConfirm(null);
      setSelectedUsers((prev) => prev.filter((id) => id !== deleteConfirm.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length > 0) {
      if (
        window.confirm(
          `Are you sure you want to delete ${selectedUsers.length} user(s)?`
        )
      ) {
        await userService.deleteUsers(selectedUsers);
        await fetchUsers();
        setSelectedUsers([]);
      }
    }
  };

  const handleFiltersChange = (newFilters: UserFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Invitation handlers
  const fetchPendingInvitations = async () => {
    setInvitationsLoading(true);
    try {
      const result = await invitationService.listPendingInvitations(1, 50);
      setPendingInvitations(result.invitations);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
    } finally {
      setInvitationsLoading(false);
    }
  };

  const handleSendInvitation = async (data: CreateInvitationInput) => {
    await invitationService.createInvitation(data);
    await fetchPendingInvitations();
  };

  const handleRevokeInvitation = async (id: string) => {
    if (window.confirm('Are you sure you want to revoke this invitation?')) {
      await invitationService.revokeInvitation(id);
      await fetchPendingInvitations();
    }
  };

  const handleResendInvitation = async (email: string) => {
    await invitationService.resendInvitation(email);
    await fetchPendingInvitations();
  };

  return (
    <AdminLayout>
      <div className={styles.container}>
        {/* Page header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Users</h1>
            <p className={styles.subtitle}>
              Manage user accounts and permissions
            </p>
          </div>
          <div className={styles.headerRight}>
            {selectedUsers.length > 0 && (
              <Button
                variant="secondary"
                onClick={handleBulkDelete}
                glass
                icon={<Icon name="trash" size="sm" />}
              >
                Delete ({selectedUsers.length})
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => setIsInviteModalOpen(true)}
              glass
              icon={<Icon name="mail" size="sm" />}
            >
              Invite User
            </Button>
            <Button
              variant="primary"
              onClick={handleCreateUser}
              glass
              icon={<Icon name="plus" size="sm" />}
            >
              New User
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'users' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Active Users ({total})
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'invitations' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('invitations')}
          >
            Pending Invitations ({pendingInvitations.length})
          </button>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'users' ? (
          <>
            {/* Filters */}
            <UserTableFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              resultCount={total}
            />

            {/* Users table */}
            <UsersTable
              users={users}
              selectedUsers={selectedUsers}
              onSelectUser={handleSelectUser}
              onSelectAll={handleSelectAll}
              onEdit={handleEditUser}
              onDelete={handleDeleteUser}
              loading={loading}
            />

            {/* Pagination */}
            {total > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                total={total}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
              />
            )}
          </>
        ) : (
          <InvitationList
            invitations={pendingInvitations}
            loading={invitationsLoading}
            onRevoke={handleRevokeInvitation}
            onResend={handleResendInvitation}
          />
        )}

        {/* User form modal */}
        <UserForm
          user={editingUser}
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingUser(null);
          }}
          onSubmit={handleFormSubmit}
        />

        {/* Invitation modal */}
        <InvitationModal
          isOpen={isInviteModalOpen}
          onClose={() => setIsInviteModalOpen(false)}
          onSubmit={handleSendInvitation}
        />

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div className={styles.deleteModal}>
            <div className={styles.deleteModalContent}>
              <div className={styles.deleteModalHeader}>
                <h3>Confirm Deletion</h3>
              </div>
              <p>
                Are you sure you want to delete{' '}
                <strong>{deleteConfirm.name}</strong>? This action cannot be
                undone.
              </p>
              <div className={styles.deleteModalActions}>
                <Button
                  variant="secondary"
                  onClick={() => setDeleteConfirm(null)}
                  glass
                >
                  Cancel
                </Button>
                <Button variant="danger" onClick={confirmDelete} glass>
                  Delete User
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};
